import { useState, useEffect, forwardRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { trackEvent, trackFormEngagement } from '@/components/GoogleAnalytics';
import { trackEvent as trackEventHj, trackFormEngagementHj } from '@/components/Hotjar';
import { trackEventMp, trackFormEngagementMp } from '@/components/Mixpanel';
import { useTranslation } from 'react-i18next';

interface NewsletterCharacterCountProps {
  current: number;
  max: number;
}

const NewsletterCharacterCount = ({ current, max }: NewsletterCharacterCountProps) => {
  return (
    <div className="text-xs text-muted-foreground/60">
      {current}/{max}
    </div>
  );
};

interface NewsletterTextareaProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  showQuickFills?: boolean;
  onClear?: () => void;
}

export const NewsletterTextarea = forwardRef<HTMLTextAreaElement, NewsletterTextareaProps>(
  ({ value, onChange, error, showQuickFills = true, onClear }, ref) => {
  const { t } = useTranslation();
  const translatedSuggestions = t('newsletterTextarea.suggestions', { returnObjects: true }) as string[];
  const translatedQuickFills = t('newsletterTextarea.quickFills', { returnObjects: true }) as Array<{label: string, content: string}>;
  
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const maxLength = 500;

  useEffect(() => {
    if (!isFocused && !value) {
      const interval = setInterval(() => {
        setCurrentSuggestion((prev) => (prev + 1) % translatedSuggestions.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isFocused, value]);

  const handleQuickFill = (content: string, label: string) => {
    const wasReplacing = value.length > 0;
    
    // Track quick suggestion click
    trackEvent('quick_suggestion_clicked', {
      event_category: 'form_interaction',
      event_label: label,
      suggestion_type: label.toLowerCase().replace(/\s+/g, '_'),
      was_replacing: wasReplacing,
      previous_value_length: value.length
    });
    trackEventMp('quick_suggestion_clicked', {
      event_category: 'form_interaction',
      event_label: label,
      suggestion_type: label.toLowerCase().replace(/\s+/g, '_'),
      was_replacing: wasReplacing,
      previous_value_length: value.length
    });
    trackEventHj(`quick_suggestion_${label.toLowerCase().replace(/\s+/g, '_')}`);
    trackFormEngagement('interests_modal', 'quick_suggestion_used');
    trackFormEngagementHj('interests_modal', 'quick_suggestion_used');
    trackFormEngagementMp('interests_modal', 'quick_suggestion_used');
    
    onChange(content);
  };

  const handleClear = () => {
    onChange('');
    
    // Call custom clear callback if provided
    if (onClear) {
      onClear();
    }
    
    trackEvent('clear_interests_clicked', {
      event_category: 'form_interaction',
      previous_length: value.length
    });
    trackEventMp('clear_interests_clicked', {
      event_category: 'form_interaction',
      previous_length: value.length
    });
    trackEventHj('clear_interests');
    trackFormEngagement('interests_modal', 'interests_cleared');
    trackFormEngagementHj('interests_modal', 'interests_cleared');
    trackFormEngagementMp('interests_modal', 'interests_cleared');
  };

  return (
    <div className="relative space-y-4">
      {/* Textarea PRIMEIRO - Principal foco */}
      <Textarea
        ref={ref}
        id="interests-textarea"
        placeholder={!isFocused && !value ? translatedSuggestions[currentSuggestion] + "..." : t('newsletterTextarea.placeholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          setIsFocused(true);
          trackFormEngagement('interests_modal', 'textarea_focused');
          trackFormEngagementHj('interests_modal', 'textarea_focused');
          trackFormEngagementMp('interests_modal', 'textarea_focused');
        }}
        onBlur={() => {
          setIsFocused(false);
          trackFormEngagement('interests_modal', 'textarea_blurred');
          trackFormEngagementHj('interests_modal', 'textarea_blurred');
          trackFormEngagementMp('interests_modal', 'textarea_blurred');
        }}
        maxLength={maxLength}
        className={cn(
          `min-h-[130px] sm:min-h-[160px] px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base rounded-2xl resize-none
          bg-background/5 backdrop-blur-sm 
          border-2 border-newsletter-primary/20 
          focus:border-newsletter-primary/50
          placeholder:text-muted-foreground/60
          transition-all duration-300 ease-out
          hover:border-newsletter-primary/30
          focus:shadow-newsletter`,
          error && 'border-destructive/50 focus:border-destructive'
        )}
      />

      {/* Quick Fill Chips DEPOIS - Como suporte */}
      {showQuickFills && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground/70 font-medium flex items-center gap-1.5">
              <span>📝</span>
              <span>{t('newsletterTextarea.quickFillLabelNew')}</span>
            </p>
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-muted-foreground/70 hover:text-destructive transition-colors flex items-center gap-1"
              >
                🗑️ {t('newsletterTextarea.clearButton')}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {translatedQuickFills.map((option, index) => {
              const isSelected = value.includes(option.content);
              return (
                <button
                  key={index}
                  id={`quick-suggestion-${option.label.toLowerCase().replace(/\s+/g, '-')}`}
                  type="button"
                  onClick={() => handleQuickFill(option.content, option.label)}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-full border transition-all duration-200",
                    isSelected
                      ? "bg-newsletter-primary text-white border-newsletter-primary shadow-lg scale-105"
                      : "bg-newsletter-primary/10 hover:bg-newsletter-primary/20 text-newsletter-primary border-newsletter-primary/20 hover:border-newsletter-primary/30 hover:scale-105"
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <div className="text-sm text-destructive">
          {error}
        </div>
        <NewsletterCharacterCount current={value.length} max={maxLength} />
      </div>
      
      {!isFocused && !value && (
        <div className="absolute inset-0 rounded-2xl bg-newsletter-gradient-subtle opacity-20 pointer-events-none animate-glow" />
      )}
    </div>
  );
});

NewsletterTextarea.displayName = 'NewsletterTextarea';